import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  Textarea,
} from "@fluentui/react-components";
import { Dismiss12Filled } from "@fluentui/react-icons";
import Dropzone from "react-dropzone";
import moment from "moment";
import apiServices from "../../../../service";
import FormikInputRequest from "../../../../service/hook/useFormik";
import ASCButton from "../../../../components/Button";
import DynamicDatePicker from "../../../../components/ControlledDatePicker/dynamicDatepicker";
import UploadIcon from "../../../../assets/uploadicon.svg";
import "../style.css";

interface DocumentActionsProps {
  currentPath: string[];
  basePath: string;
  activeCustomerId: string;
  notify: (msg: string, sub: string, type: string) => void;
  onRefresh: () => void;
}

type DialogAction = "addFolder" | "addDoc" | "";

const DocumentActions: React.FC<DocumentActionsProps> = ({
  currentPath,
  basePath,
  activeCustomerId,
  notify,
  onRefresh,
}) => {
  const [dialogAction, setDialogAction] = useState<DialogAction>("");

  return (
    <>
      <div className="doc-actions-bar">
        <ASCButton
          shape="rounded"
          // appearance="primary"
          // className="asc-button-primary"
          onClick={() => setDialogAction("addFolder")}
          label="Add Folder"
        />
        <ASCButton
          shape="rounded"
          appearance="primary"
          className="asc-button-primary"
          onClick={() => setDialogAction("addDoc")}
          label="Add Document"
        />
      </div>

      <AddFolderDialog
        isOpen={dialogAction === "addFolder"}
        onClose={() => setDialogAction("")}
        currentPath={currentPath}
        basePath={basePath}
        activeCustomerId={activeCustomerId}
        notify={notify}
        onRefresh={onRefresh}
      />

      <AddDocumentDialog
        isOpen={dialogAction === "addDoc"}
        onClose={() => setDialogAction("")}
        currentPath={currentPath}
        basePath={basePath}
        activeCustomerId={activeCustomerId}
        notify={notify}
        onRefresh={onRefresh}
      />
    </>
  );
};

// ─── Add Folder Dialog ───────────────────────────────────────────────────────

interface AddFolderDialogProps {
  isOpen: boolean;
  onClose: () => void;
  currentPath: string[];
  basePath: string;
  activeCustomerId: string;
  notify: (msg: string, sub: string, type: string) => void;
  onRefresh: () => void;
}

function AddFolderDialog({
  isOpen,
  onClose,
  currentPath,
  basePath,
  activeCustomerId,
  notify,
  onRefresh,
}: AddFolderDialogProps) {
  const pathStr = currentPath.join("/");

  const formik = FormikInputRequest({
    initialValues: { folderName: "" },
    onSubmitFn: async (values: any) => {
      const folderPath = pathStr ? `${basePath}/${pathStr}/` : `${basePath}/`;
      const folderNameWithPath = `${folderPath}${values.folderName}/`;
      try {
        const res = await apiServices.clientDocument.addFolder({
          folderName: folderNameWithPath,
          customer_id: activeCustomerId,
          visible: "public",
        });
        setTimeout(() => {
          notify(res.data.message, "", "success");
          onRefresh();
        }, 1000);
        onClose();
        formik.resetForm();
      } catch (error) {
        console.error("Error adding folder:", error);
      }
    },
    validate: (values: any) => {
      const errors: any = {};
      if (!values.folderName) {
        errors.folderName = "Folder Name is required";
      } else if (values.folderName.length > 256) {
        errors.folderName = "Folder Name cannot be longer than 256 characters";
      }
      return errors;
    },
  });

  const handleCancel = () => {
    onClose();
    formik.resetForm();
  };

  return (
    <Dialog open={isOpen}>
      <DialogSurface className="dialog-surface">
        <DialogTitle>Add Folder</DialogTitle>
        <DialogContent>
          <div className="folder_doc">
            <Field
              label={
                <span>
                  Folder Name
                  {formik.errors.folderName && (
                    <span className="required-item">*</span>
                  )}
                </span>
              }
              validationState={formik.errors.folderName ? "error" : "none"}
              validationMessage={`${formik.errors.folderName || ""}`}
            >
              <Input
                appearance="outline"
                name="folderName"
                value={formik.values.folderName}
                onChange={(e) =>
                  formik.setFieldValue("folderName", e.target.value)
                }
              />
            </Field>
          </div>
        </DialogContent>
        <DialogActions className="flex-end">
          <ASCButton shape="rounded" onClick={handleCancel} label="Cancel" />
          <ASCButton
            shape="rounded"
            appearance="primary"
            onClick={() => formik.handleSubmit()}
            style={{ background: "#0f6cbd" }}
            label="Save"
          />
        </DialogActions>
      </DialogSurface>
    </Dialog>
  );
}

// ─── Add Document Dialog ─────────────────────────────────────────────────────

interface AddDocumentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  currentPath: string[];
  basePath: string;
  activeCustomerId: string;
  notify: (msg: string, sub: string, type: string) => void;
  onRefresh: () => void;
}

function AddDocumentDialog({
  isOpen,
  onClose,
  currentPath,
  basePath,
  activeCustomerId,
  notify,
  onRefresh,
}: AddDocumentDialogProps) {
  const pathStr = currentPath.join("/");
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

  const formik = FormikInputRequest({
    initialValues: {
      file_name: "",
      date: new Date(),
      description: "",
      file: "",
    },
    onSubmitFn: async (values: any) => {
      const formData = new FormData();
      formData.append("file_name", values.file_name);
      formData.append("folder_name", `${basePath}/${pathStr}`);
      formData.append("date", values.date);
      formData.append("description", values.description);
      formData.append("file", values.file);
      formData.append("customer_id", activeCustomerId);
      formData.append("visible", "public");
      try {
        const res = await apiServices.clientDocument.addDocument(formData);
        setTimeout(() => {
          onRefresh();
          notify(res.data.message, "", "success");
        }, 1000);
        onClose();
        setUploadedFiles([]);
        formik.resetForm();
      } catch (error) {
        console.error("Error adding document:", error);
      }
    },
    validate: (values: any) => {
      const errors: any = {};
      const MAX_FILE_SIZE = 2 * 1024 * 1024;
      if (values.file_name.length > 256) {
        errors.file_name = "File Name cannot be longer than 256 characters";
      }
      if (values.description && values.description.length > 5000) {
        errors.description = "Description cannot be longer than 5000 characters";
      }
      if (!values.file) {
        errors.file = "File is required";
      } else if (values.file.size > MAX_FILE_SIZE) {
        errors.file = "File size must be less than 2MB";
      }
      return errors;
    },
  });

  const handleCancel = () => {
    onClose();
    formik.resetForm();
    setUploadedFiles([]);
  };

  const removeFile = () => {
    setUploadedFiles([]);
    formik.setFieldValue("file", "");
    formik.setFieldValue("file_name", "");
  };

  return (
    <Dialog open={isOpen}>
      <DialogSurface className="dialog-surface">
        <DialogTitle>Add Document</DialogTitle>
        <DialogContent>
          <div className="folder_doc">
            <Field
              label={
                <span>
                  Upload File
                  {formik.errors.file && (
                    <span className="required-item">*</span>
                  )}
                </span>
              }
              validationState={formik.errors.file ? "error" : "none"}
              validationMessage={`${formik.errors.file || ""}`}
            >
              <div className="flex-block-doc">
                <Dropzone
                  multiple={false}
                  onDrop={(acceptedFiles: File[]) => {
                    if (acceptedFiles.length > 0) {
                      const file = acceptedFiles[0];
                      setUploadedFiles([file]);
                      formik.setFieldValue("file", file, false);
                      formik.setFieldValue("file_name", file.name, false);
                    }
                  }}
                >
                  {({ getRootProps, getInputProps }) => (
                    <section className="container">
                      <div
                        {...getRootProps()}
                        className="file-drop"
                        style={{ textAlign: "center" }}
                      >
                        <input {...getInputProps()} />
                        <img src={UploadIcon} width="50px" alt="upload" />
                        <p style={{ textAlign: "center" }}>
                          Click or Drag the file here to upload
                        </p>
                      </div>
                    </section>
                  )}
                </Dropzone>
                <ul>
                  {uploadedFiles.map((file: any, index: number) => (
                    <div className="list-upload-item" key={index}>
                      <li>
                        {file.name} — {(file.size / 1024).toFixed(2)} KB
                        <Dismiss12Filled onClick={removeFile} />
                      </li>
                    </div>
                  ))}
                </ul>
              </div>
            </Field>
          </div>

          <div className="folder_doc">
            <Field
              label={
                <span>
                  File Name
                  {formik.errors.file_name && (
                    <span className="required-item">*</span>
                  )}
                </span>
              }
              validationState={formik.errors.file_name ? "error" : "none"}
              validationMessage={`${formik.errors.file_name || ""}`}
            >
              <Input
                appearance="outline"
                name="file_name"
                value={formik.values.file_name}
                onChange={(e) =>
                  formik.setFieldValue("file_name", e.target.value)
                }
              />
            </Field>
          </div>

          <div className="folder_doc">
            <Field label="Folder Name">
              <Input appearance="outline" disabled value={pathStr} />
            </Field>
          </div>

          <div className="folder_doc">
            <Field label="Date Filed">
              <DynamicDatePicker
                formik={formik}
                border
                name="date"
                customHandleChange={(e: any) => {
                  formik.setFieldValue(e.target.name, e.target.value);
                }}
              />
            </Field>
          </div>

          <div className="folder_doc">
            <Field
              label={
                <span>
                  Description
                  {formik.errors.description && (
                    <span className="required-item">*</span>
                  )}
                </span>
              }
              validationState={formik.errors.description ? "error" : "none"}
              validationMessage={`${formik.errors.description || ""}`}
            >
              <Textarea
                appearance="outline"
                style={{ backgroundColor: "#fff" }}
                value={formik.values.description}
                onChange={(e) =>
                  formik.setFieldValue("description", e.target.value)
                }
              />
            </Field>
          </div>
        </DialogContent>
        <DialogActions className="flex-end">
          <ASCButton shape="rounded" onClick={handleCancel} label="Cancel" />
          <ASCButton
            shape="rounded"
            appearance="primary"
            onClick={() => formik.handleSubmit()}
            style={{ background: "#0f6cbd" }}
            label="Save"
          />
        </DialogActions>
      </DialogSurface>
    </Dialog>
  );
}

export default DocumentActions;
